import { Suspense, lazy } from "react";
import { FileText, Anchor } from "lucide-react";
import Reveal from "./Reveal";

const Globe3DEnhanced = lazy(() => import("./Globe3DEnhanced"));

const DOC_BADGES = [
  "Invoice & Packing List",
  "Certificate of Origin",
  "Quality Inspection Report",
  "Customs Documentation",
];

const LOGISTICS = [
  "Sea Freight (FCL/LCL) & Air Freight",
  "Export-grade packaging for all categories",
  "Major Indian ports (Mumbai, Nhava Sheva, Mundra)",
];

const INCOTERMS = ["FOB", "CIF", "DDP", "EXW"];

const STAT_CHIPS = ["15+ Export Markets", "600+ SKUs", "100% QC Inspected"];

function GlobeFallback() {
  return (
    <svg viewBox="0 0 400 400" className="w-full h-full" aria-hidden>
      <circle cx="200" cy="200" r="130" fill="none" stroke="#C9A227" strokeWidth="1.2" opacity="0.6" />
      <rect x="300" y="120" width="28" height="28" fill="none" stroke="#C9A227" strokeWidth="1" />
      <circle cx="90" cy="260" r="16" fill="none" stroke="#C9A227" strokeWidth="1" />
    </svg>
  );
}

export default function GlobalTradeNetwork() {
  return (
    <section id="network" className="section-shell bg-surface-alt/40">
      <div className="section-inner space-y-8 md:space-y-10">
        {/* Crafted for Global Markets — enhanced 3D */}
        <Reveal>
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-10 items-center">
            <div>
              <p className="label-cap text-accent mb-3">Global Reach</p>
              <h2 className="font-serif text-3xl md:text-5xl font-bold text-primary leading-tight">
                Crafted for Global Markets
              </h2>
              <p className="mt-5 text-muted text-lg leading-relaxed max-w-lg">
                From sourcing hubs across India to retail shelves worldwide — end-to-end export
                partnerships built on trust.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                {STAT_CHIPS.map((chip) => (
                  <span
                    key={chip}
                    className="px-4 py-2 rounded-full text-xs font-semibold bg-surface border border-border text-primary"
                  >
                    {chip}
                  </span>
                ))}
              </div>
            </div>
            <div className="w-full max-w-[500px] mx-auto lg:ml-auto aspect-square">
              <Suspense fallback={<GlobeFallback />}>
                <Globe3DEnhanced />
              </Suspense>
            </div>
          </div>
        </Reveal>

        {/* Sourcing & Logistics card */}
        <Reveal delay={100}>
          <div className="bg-surface border border-border rounded-3xl overflow-hidden shadow-sm">
            <div className="grid lg:grid-cols-2 gap-0">
              <div className="p-8 md:p-12 lg:p-14 border-b lg:border-b-0 lg:border-r border-border">
                <div className="flex items-center gap-2 mb-3">
                  <FileText size={18} className="text-accent" />
                  <h3 className="font-serif text-2xl font-bold text-primary">Sourcing Network</h3>
                </div>
                <p className="text-xs label-cap text-muted mb-4">Export Documentation</p>
                <div className="flex flex-wrap gap-2">
                  {DOC_BADGES.map((b) => (
                    <span
                      key={b}
                      className="px-3 py-1 rounded-full text-[11px] font-medium bg-surface-alt border border-border text-primary"
                    >
                      {b}
                    </span>
                  ))}
                </div>
              </div>

              <div className="p-8 md:p-12 lg:p-14">
                <div className="flex items-center gap-2 mb-3">
                  <Anchor size={18} className="text-accent-2" />
                  <h3 className="font-serif text-2xl font-bold text-primary">Shipping &amp; Logistics</h3>
                </div>
                <ul className="space-y-2 mb-8">
                  {LOGISTICS.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-muted">
                      <span className="w-1.5 h-1.5 rounded-full bg-accent-2 mt-2 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="text-xs label-cap text-muted mb-2">Incoterms</p>
                <div className="flex flex-wrap gap-2">
                  {INCOTERMS.map((term) => (
                    <span
                      key={term}
                      className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-primary text-bg"
                    >
                      {term}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
